import axios from "axios";
import { API_CONFIG } from "../core/config/api";
import {
  ActivatePayload,
  AuthResponse,
  OtpRequestPayload,
  OtpRequestResponse,
  OtpVerifyPayload,
  OtpVerifyResponse,
  PasswordResetPayload,
  PasswordResetResponse,
  RegisterPayload,
} from "../types/models/auth";
 
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
});
 
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
 
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        const { data } = await apiClient.post<AuthResponse>(API_CONFIG.ENDPOINTS.REFRESH_TOKEN, {
          refreshToken,
        });
        localStorage.setItem("accessToken", data.access_token);
        localStorage.setItem("refreshToken", data.refresh_token || "");
        return apiClient(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);
 
export const authService = {
  // turnstileToken: Cloudflare Turnstile token from the signup form, checked by the backend.
  register: async (payload: RegisterPayload, turnstileToken?: string | null) => {
    const { data } = await apiClient.post<AuthResponse>(
      API_CONFIG.ENDPOINTS.REGISTER,
      payload,
      turnstileToken ? { headers: { "X-Turnstile-Token": turnstileToken } } : undefined
    );
    return data;
  },
 
  otpRequest: async (payload: OtpRequestPayload) => {
    try {
      const { data } = await apiClient.post<OtpRequestResponse>(API_CONFIG.ENDPOINTS.OTP_REQUEST, payload);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || "Failed to send OTP";
        throw new Error(message);
      }
      throw new Error("Failed to send OTP");
    }
  },
 
  otpVerify: async (payload: OtpVerifyPayload) => {
    try {
      const { data } = await apiClient.post<OtpVerifyResponse>(API_CONFIG.ENDPOINTS.OTP_VERIFY, payload);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || "Failed to verify OTP";
        throw new Error(message);
      }
      throw new Error("Failed to verify OTP");
    }
  },
 
  passwordReset: async (payload: PasswordResetPayload) => {
    try {
      const { data } = await apiClient.post<PasswordResetResponse>(API_CONFIG.ENDPOINTS.PASSWORD_RESET, payload);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || "Failed to reset password";
        throw new Error(message);
      }
      throw new Error("Failed to reset password");
    }
  },
 
  login: async (credentials: { email: string; password: string }) => {
    try {
      const { data } = await apiClient.post<AuthResponse>(API_CONFIG.ENDPOINTS.LOGIN, credentials);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.detail || (error.response?.data as any)?.message || "Login failed";
        throw new Error(message);
      }
      throw new Error("Login failed");
    }
  },
 
  loginAsCustomer: async (credentials: { email: string; password: string; user_id: number }) => {
    try {
      const { data } = await apiClient.post<AuthResponse>(API_CONFIG.ENDPOINTS.LOGIN_AS_CUSTOMER, credentials);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as { message?: string } | undefined)?.message || "Login as customer failed";
        throw new Error(message);
      }
      throw new Error("Login as customer failed");
    }
  },

  googleLogin: async (payload: {
    access_token: string;
    email: string;
    first_name: string;
    last_name: string;
    google_id: string;
    picture: string;
  }) => {
    try {
      const { data } = await apiClient.post<AuthResponse>(
        API_CONFIG.ENDPOINTS.GOOGLE_LOGIN,
        payload
      );
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as any)?.detail ||
          (error.response?.data as any)?.message ||
          "Google login failed";
        throw new Error(message);
      }
      throw new Error("Google login failed");
    }
  },
 
  microsoftLogin: async (payload: {
    id_token: string;
    email: string;
    first_name: string;
    last_name: string;
    microsoft_id: string;
    picture: string;
  }) => {
    try {
      const { data } = await apiClient.post<AuthResponse>(
        API_CONFIG.ENDPOINTS.MICROSOFT_LOGIN,
        payload
      );
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as any)?.detail ||
          (error.response?.data as any)?.message ||
          "Microsoft login failed";
        throw new Error(message);
      }
      throw new Error("Microsoft login failed");
    }
  },
 
  activate: async (payload: ActivatePayload) => {
    try {
      const { data } = await apiClient.post<AuthResponse>(API_CONFIG.ENDPOINTS.ACTIVATE, payload);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.response?.data?.message || "Activation failed";
        throw new Error(message);
      }
      throw new Error("Activation failed");
    }
  },

  linkedinLogin: async (payload: { code: string }) => {
    try {
      const { data } = await apiClient.post<AuthResponse>(
        API_CONFIG.ENDPOINTS.LINKEDIN_LOGIN,
        payload
      );
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as any)?.detail ||
          (error.response?.data as any)?.message ||
          "LinkedIn login failed";
        throw new Error(message);
      }
      throw new Error("LinkedIn login failed");
    }
  },
};